import React, { useRef, useState } from 'react';
import { useEditorActions, useSelectedId } from '../store/editorStore.jsx';
import { DND_BLOCK } from '../dnd/dndTypes.js';
import { Inserter } from './Inserter.jsx';

/**
 * BlockFrame
 *
 * Wraps each block in the canvas with:
 *  - selection highlight
 *  - drag handle (⠿) for reordering
 *  - action bar (move up/down, duplicate, delete)
 *  - "+" inserter above/below when selected
 *  - drop indicator line for drag-and-drop
 *
 * Props:
 *   block        {object}        – the block being framed
 *   parentId     {string|null}   – container id (null = root)
 *   index        {number}        – index within parent
 *   siblingCount {number}        – total siblings (disables move buttons at edges)
 *   onDropBefore (id, toIndex)   – called when a block is dropped before this one
 *   children     {ReactNode}     – the rendered block Canvas component
 */
export function BlockFrame({ block, parentId, index, siblingCount, onDropBefore, children }) {
  const selectedId = useSelectedId();
  const { selectBlock, removeBlock, duplicateBlock, moveBlock, insertBlock } = useEditorActions();
  const isSelected = selectedId === block.id;
  const frameRef = useRef(null);

  const [inserterAboveOpen, setInserterAboveOpen] = useState(false);
  const [inserterBelowOpen, setInserterBelowOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // ── Drag source ─────────────────────────────────────────────────────────
  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.setData(DND_BLOCK, block.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  // ── Drop target (drop *before* this block) ────────────────────────────
  const handleDragOver = (e) => {
    if (!e.dataTransfer.types.includes(DND_BLOCK)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData(DND_BLOCK);
    if (!draggedId || draggedId === block.id) return;
    onDropBefore && onDropBefore(draggedId, index);
  };

  const handleDragEnd = () => setIsDragOver(false);

  // ── Close inserters when clicking outside ──────────────────────────────
  React.useEffect(() => {
    if (!inserterAboveOpen && !inserterBelowOpen) return;
    const handler = () => {
      setInserterAboveOpen(false);
      setInserterBelowOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [inserterAboveOpen, inserterBelowOpen]);

  return (
    <div
      ref={frameRef}
      data-block-id={block.id}
      data-block-type={block.type}
      className={`editor-block-frame${isSelected ? ' editor-block-frame--selected' : ''}${isDragOver ? ' editor-block-frame--drag-over' : ''}`}
      style={{
        position: 'relative',
        outline: isSelected
          ? '2px solid #0d6efd'
          : isDragOver
            ? '2px dashed #0d6efd'
            : '1px solid #dee2e6',
        borderRadius: 4,
        marginBottom: 4,
        transition: 'outline 0.1s',
      }}
      onClick={(e) => { e.stopPropagation(); selectBlock(block.id); }}
    >
      {/* Drop indicator line above */}
      {isDragOver && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -2,
            left: 0,
            right: 0,
            height: 3,
            background: '#0d6efd',
            borderRadius: 2,
            zIndex: 10,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Invisible drag-over capture zone */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, zIndex: 3 }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      />

      {/* Floating action bar (visible when selected) */}
      {isSelected && (
        <div
          className="editor-block-actions"
          style={{
            position: 'absolute',
            top: 2,
            right: 4,
            display: 'flex',
            gap: 2,
            zIndex: 20,
            background: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: 4,
            padding: '1px 2px',
            boxShadow: '0 1px 4px rgba(0,0,0,.12)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span
            title="Drag to reorder"
            style={{ cursor: 'grab', padding: '2px 4px', fontSize: '0.8rem', color: '#6c757d' }}
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            ⠿
          </span>
          <ActionBtn title="Move up" disabled={index === 0} onClick={() => moveBlock(block.id, parentId, index - 1)}>↑</ActionBtn>
          <ActionBtn title="Move down" disabled={index >= siblingCount - 1} onClick={() => moveBlock(block.id, parentId, index + 1)}>↓</ActionBtn>
          <ActionBtn title="Duplicate" onClick={() => duplicateBlock(block.id)}>⎘</ActionBtn>
          <ActionBtn title="Delete block" color="#dc3545" onClick={() => removeBlock(block.id)}>✕</ActionBtn>
        </div>
      )}

      {/* Inserter above */}
      {isSelected && (
        <div
          style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', zIndex: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-primary rounded-circle p-0"
            style={{ width: 22, height: 22, fontSize: '0.75rem', lineHeight: '22px' }}
            title="Insert block above"
            onClick={() => setInserterAboveOpen((v) => !v)}
          >
            +
          </button>
          {inserterAboveOpen && (
            <div style={{ position: 'absolute', top: 26, left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
              <Inserter
                parentId={parentId}
                index={index}
                onInsert={(type) => { insertBlock(parentId, index, type); setInserterAboveOpen(false); }}
                onClose={() => setInserterAboveOpen(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* Block content */}
      <div className="editor-block-content" style={{ padding: '6px 8px', position: 'relative', zIndex: 4 }}>
        {children}
      </div>

      {/* Inserter below */}
      {isSelected && (
        <div
          style={{ position: 'absolute', bottom: -11, left: '50%', transform: 'translateX(-50%)', zIndex: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-primary rounded-circle p-0"
            style={{ width: 22, height: 22, fontSize: '0.75rem', lineHeight: '22px' }}
            title="Insert block below"
            onClick={() => setInserterBelowOpen((v) => !v)}
          >
            +
          </button>
          {inserterBelowOpen && (
            <div style={{ position: 'absolute', top: 26, left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
              <Inserter
                parentId={parentId}
                index={index + 1}
                onInsert={(type) => { insertBlock(parentId, index + 1, type); setInserterBelowOpen(false); }}
                onClose={() => setInserterBelowOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ title, disabled, onClick, color = '#6c757d', children }) {
  return (
    <button
      type="button"
      title={title}
      className="btn p-0"
      style={{
        fontSize: '0.75rem',
        lineHeight: 1.4,
        color: disabled ? '#ced4da' : color,
        background: 'none',
        border: 'none',
        padding: '1px 3px',
      }}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {children}
    </button>
  );
}
